import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { LargeCircularProgress } from "@/components/LargeCircularProgress";
import { YourChallengeStats } from "@/components/YourChallengeStats";
import { MarathonProgressSection } from "@/components/MarathonProgressSection";
import { StatInfoCard } from "@/components/StatInfoCard";
import { ChallengeTabNavigation } from "@/components/ChallengeTabNavigation";
import { PodiumDisplay } from "@/components/PodiumDisplay";
import { LeaderboardItem } from "@/components/LeaderboardItem";
import { ErrorToast } from "@/components/ErrorToast";
import { ChallengeSummary } from "@/components/ChallengeSummary";
import { ParticipantProfileModal } from "@/components/ParticipantProfileModal";
import { StickyHeader } from "@/components/ui/StickyHeader";
import { HeaderIconButton } from "@/components/ui/HeaderIconButton";
import { progressApi } from "@/api/progress";
import { useAuth } from "@/contexts/AuthContext";
import { useChallengeProgress } from "@/hooks/useChallengeProgress";
import { useLeaderboard, LeaderboardParticipant } from "@/hooks/useLeaderboard";
import { useChallengeSummary } from "@/hooks/useChallengeSummary";
import {
  formatMarathonProgress,
  MARATHON_DISTANCE_KM,
} from "@/utils/formatters";

// Default user avatar for stats card
const DEFAULT_USER_AVATAR = "https://api.dicebear.com/7.x/avataaars/svg?seed=default";

export function ChallengeDetail() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<"leaderboard" | "rules" | "feed">(
    "leaderboard"
  );
  const [selectedParticipant, setSelectedParticipant] = useState<LeaderboardParticipant | null>(null);

  // Challenge progress stats (user's own stats)
  const { data: progressData, isLoading: isLoadingProgress, error: progressError, refetch: refetchProgress } = useChallengeProgress(id);

  // Leaderboard data (all participants ranked)
  const { data: leaderboardData, isLoading: isLoadingLeaderboard, error: leaderboardError, refetch: refetchLeaderboard } = useLeaderboard(id);

  // Check if challenge is completed (for showing summary)
  const isCompleted = progressData?.challengeStatus === 'completed';

  // Check if this is a marathon challenge
  const isMarathon = progressData?.challengeType === 'marathon' || progressData?.isOffChain;

  // Challenge summary data (only fetch if completed)
  const { data: summaryData, isLoading: isLoadingSummary } = useChallengeSummary(id, isCompleted);

  // Progress sync state
  const [isSyncing, setIsSyncing] = useState(false);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastVariant, setToastVariant] = useState<
    "error" | "warning" | "info" | "success"
  >("success");

  // Derive leaderboard sections from API data
  const participants = leaderboardData?.participants || [];
  const topThree = participants.slice(0, 3);
  const restOfLeaderboard = participants.slice(3);
  const totalProgress = leaderboardData?.requiredDays || progressData?.totalDays || 30;

  // Show error toast if progress or leaderboard fetch fails
  useEffect(() => {
    const error = progressError || leaderboardError;
    if (error) {
      setToastMessage(error);
      setToastVariant("error");
      setToastOpen(true);
    }
  }, [progressError, leaderboardError]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleShare = () => {
    // TODO: Implement share functionality
  };

  const handleLogActivity = async () => {
    if (!id) {
      setToastMessage("Challenge ID not found");
      setToastVariant("error");
      setToastOpen(true);
      return;
    }

    if (!user?.walletAddress) {
      setToastMessage("Please connect your wallet first");
      setToastVariant("warning");
      setToastOpen(true);
      return;
    }

    setIsSyncing(true);

    try {
      const result = await progressApi.syncProgress(id);

      if (result.success) {
        setToastMessage(
          `Synced ${result.activitiesSynced} ${
            result.activitiesSynced === 1 ? "activity" : "activities"
          } from Strava!`
        );
        setToastVariant("success");
        setToastOpen(true);

        // Refresh challenge data to show updated progress
        await Promise.all([refetchProgress(), refetchLeaderboard()]);
      } else {
        setToastMessage(result.message || "Failed to sync activities");
        setToastVariant("error");
        setToastOpen(true);
      }
    } catch (error: any) {
      console.error("Error syncing progress:", error);

      // Handle specific error messages from backend
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to sync activities. Please try again.";

      setToastMessage(errorMessage);
      setToastVariant("error");
      setToastOpen(true);
    } finally {
      setIsSyncing(false);
    }
  };

  // Handler for participant click - find participant by wallet address
  const handleParticipantClick = (walletAddress: string) => {
    const participant = participants.find(
      (p) => p.walletAddress.toLowerCase() === walletAddress.toLowerCase()
    );
    if (participant) {
      setSelectedParticipant(participant);
    }
  };

  // Show full-page loading state while fetching data
  if (isLoadingProgress || isLoadingLeaderboard || (isCompleted && isLoadingSummary)) {
    return (
      <div className="relative flex min-h-screen w-full flex-col items-center justify-center bg-background-light dark:bg-background-dark text-content-light dark:text-content-dark pt-[env(safe-area-inset-top)]">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
          <p className="text-content-light/70 dark:text-content-dark/70 text-sm">Loading challenge...</p>
        </div>
      </div>
    );
  }

  // Show Challenge Summary for completed challenges
  if (isCompleted && summaryData) {
    return (
      <ChallengeSummary
        data={summaryData}
        userAvatar={user?.avatarUrl || DEFAULT_USER_AVATAR}
        onClose={handleBack}
        onShare={handleShare}
      />
    );
  }

  return (
    <div className="relative flex h-auto min-h-screen w-full flex-col bg-background-light dark:bg-background-dark text-content-light dark:text-content-dark">
      {/* Sticky Header with safe-area */}
      <StickyHeader
        transparent
        leftContent={
          <HeaderIconButton
            icon="arrow_back"
            variant="subtle"
            onClick={handleBack}
            ariaLabel="Go back"
          />
        }
        title={progressData?.challengeName || "Challenge"}
        rightContent={
          <HeaderIconButton
            icon="ios_share"
            variant="subtle"
            onClick={handleShare}
            ariaLabel="Share"
          />
        }
        className="z-20"
      />

      <main className="flex-1 pb-32">
        {/* Progress Section - Marathon vs Regular */}
        {isMarathon ? (
          <MarathonProgressSection
            userAvatar={user?.avatarUrl || DEFAULT_USER_AVATAR}
            totalDistance={progressData?.totalDistance ?? 0}
            totalDuration={progressData?.totalDuration ?? 0}
            targetDurationSeconds={progressData?.targetDurationSeconds ?? null}
            averagePace={progressData?.averagePace ?? null}
          />
        ) : (
          <>
            {/* Large Circular Progress */}
            <div className="p-4 pt-6 flex flex-col items-center text-center">
              <LargeCircularProgress
                currentDay={progressData?.currentDay ?? 1}
                totalDays={progressData?.totalDays ?? totalProgress}
              />
              <p className="mt-4 text-base font-medium text-primary">
                Ends in {progressData?.daysLeft ?? 0} days
              </p>
            </div>

            {/* Your Challenge Stats */}
            {isLoadingProgress ? (
              <div className="px-4 py-2">
                <div className="rounded-4xl bg-gradient-to-br from-primary/20 via-primary/10 to-transparent p-4 border border-primary/40 animate-pulse">
                  <div className="flex items-center gap-3">
                    <div className="size-12 rounded-full bg-black/10 dark:bg-white/10" />
                    <div className="flex-1">
                      <div className="h-5 w-40 bg-black/10 dark:bg-white/10 rounded mb-2" />
                      <div className="h-3 w-32 bg-black/10 dark:bg-white/10 rounded" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 mt-4">
                    <div className="col-span-2 h-24 rounded-4xl bg-black/5 dark:bg-white/5" />
                    <div className="h-36 rounded-4xl bg-black/5 dark:bg-white/5" />
                    <div className="h-36 rounded-4xl bg-black/5 dark:bg-white/5" />
                  </div>
                </div>
              </div>
            ) : progressData ? (
              <YourChallengeStats
                userAvatar={user?.avatarUrl || DEFAULT_USER_AVATAR}
                totalDistance={progressData.totalDistance}
                distanceGoal={progressData.distanceGoal}
                averagePace={progressData.averagePace}
                bestDay={progressData.bestDay}
                activeDays={progressData.activeDays}
                totalActiveDays={progressData.totalActiveDays}
                recentActivities={progressData.recentActivities}
              />
            ) : null}
          </>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-2 gap-4 px-4 mt-6">
          <StatInfoCard
            label="Total Staked"
            value={`$${progressData?.totalPrizePool ?? 0}`}
            icon="emoji_events"
          />
          <StatInfoCard
            label="Your Stake"
            value={`$${progressData?.userStakePaid ?? 0}`}
            icon="monetization_on"
          />
        </div>

        {/* Tab Navigation */}
        <ChallengeTabNavigation
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />

        {/* Leaderboard Content */}
        {activeTab === "leaderboard" && (
          <div className="p-4 flex flex-col gap-4">
            {/* Participants Header */}
            <div className="flex items-center justify-between">
              <p className="text-lg font-bold text-content-light dark:text-content-dark">Participants</p>
              <div className="flex items-center justify-start -space-x-3">
                {/* Show first 3 participant avatars */}
                {participants.slice(0, 3).map((p) => (
                  <div
                    key={p.walletAddress}
                    className="bg-center bg-no-repeat aspect-square bg-cover rounded-full flex items-center justify-center size-8 border-2 border-background-light dark:border-background-dark"
                    style={{ backgroundImage: `url("${p.avatar}")` }}
                  />
                ))}
                {/* Show remaining count if more than 3 participants */}
                {leaderboardData && leaderboardData.participantCount > 3 && (
                  <div className="bg-primary/30 text-primary aspect-square rounded-full flex items-center justify-center size-8 border-2 border-background-light dark:border-background-dark text-xs font-bold">
                    +{leaderboardData.participantCount - 3}
                  </div>
                )}
              </div>
            </div>

            {/* Podium Display - only show if we have at least 3 participants */}
            {topThree.length >= 3 ? (
              <div className="flex flex-col gap-3">
                <PodiumDisplay
                  first={{
                    rank: topThree[0].rank,
                    name: topThree[0].name,
                    avatar: topThree[0].avatar,
                    progress: isMarathon
                      ? formatMarathonProgress(topThree[0].totalRunDistance, topThree[0].totalRunDuration)
                      : `${topThree[0].daysCompleted}/${totalProgress}`,
                    isWinner: topThree[0].isWinner,
                    walletAddress: topThree[0].walletAddress,
                  }}
                  second={{
                    rank: topThree[1].rank,
                    name: topThree[1].name,
                    avatar: topThree[1].avatar,
                    progress: isMarathon
                      ? formatMarathonProgress(topThree[1].totalRunDistance, topThree[1].totalRunDuration)
                      : `${topThree[1].daysCompleted}/${totalProgress}`,
                    walletAddress: topThree[1].walletAddress,
                  }}
                  third={{
                    rank: topThree[2].rank,
                    name: topThree[2].name,
                    avatar: topThree[2].avatar,
                    progress: isMarathon
                      ? formatMarathonProgress(topThree[2].totalRunDistance, topThree[2].totalRunDuration)
                      : `${topThree[2].daysCompleted}/${totalProgress}`,
                    walletAddress: topThree[2].walletAddress,
                  }}
                  onParticipantClick={handleParticipantClick}
                />

                {/* Rest of Leaderboard */}
                {restOfLeaderboard.length > 0 && (
                  <div className="flex flex-col gap-3 mt-6">
                    {restOfLeaderboard.map((participant) => (
                      <LeaderboardItem
                        key={participant.walletAddress}
                        rank={participant.rank}
                        name={participant.name}
                        avatar={participant.avatar}
                        currentProgress={isMarathon ? participant.totalRunDistance : participant.daysCompleted}
                        totalProgress={isMarathon ? MARATHON_DISTANCE_KM : totalProgress}
                        isCurrentUser={participant.isCurrentUser}
                        onClick={() => handleParticipantClick(participant.walletAddress)}
                        customProgressDisplay={isMarathon ? formatMarathonProgress(participant.totalRunDistance, participant.totalRunDuration) : undefined}
                      />
                    ))}
                  </div>
                )}
              </div>
            ) : participants.length > 0 ? (
              // Show simple list if less than 3 participants
              <div className="flex flex-col gap-3">
                {participants.map((participant) => (
                  <LeaderboardItem
                    key={participant.walletAddress}
                    rank={participant.rank}
                    name={participant.name}
                    avatar={participant.avatar}
                    currentProgress={isMarathon ? participant.totalRunDistance : participant.daysCompleted}
                    totalProgress={isMarathon ? MARATHON_DISTANCE_KM : totalProgress}
                    isCurrentUser={participant.isCurrentUser}
                    onClick={() => handleParticipantClick(participant.walletAddress)}
                    customProgressDisplay={isMarathon ? formatMarathonProgress(participant.totalRunDistance, participant.totalRunDuration) : undefined}
                  />
                ))}
              </div>
            ) : (
              // Empty state
              <div className="text-center py-8">
                <p className="text-content-light/50 dark:text-content-dark/50">
                  No participants yet. Be the first to join!
                </p>
              </div>
            )}
          </div>
        )}

        {/* Rules Content */}
        {activeTab === "rules" && (
          <div className="p-4">
            {isMarathon ? (
              <div className="space-y-4">
                <div className="rounded-2xl bg-black/5 dark:bg-white/5 p-4">
                  <h3 className="font-bold text-content-light dark:text-content-dark mb-2">Marathon Challenge</h3>
                  <ul className="space-y-2 text-sm text-content-light/70 dark:text-content-dark/70">
                    <li>• Complete a full marathon distance ({MARATHON_DISTANCE_KM} km)</li>
                    <li>• Finish within the target time to win</li>
                    <li>• Log your runs via Strava to track progress</li>
                    <li>• Winners are determined by completion + time</li>
                  </ul>
                </div>
              </div>
            ) : (
              <p className="text-content-light/70 dark:text-content-dark/70 text-center">
                Rules content coming soon...
              </p>
            )}
          </div>
        )}

        {/* Feed Content */}
        {activeTab === "feed" && (
          <div className="p-4">
            <p className="text-content-light/70 dark:text-content-dark/70 text-center">
              Feed content coming soon...
            </p>
          </div>
        )}
      </main>

      {/* Bottom Action Button */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-background-light via-background-light to-transparent dark:from-background-dark dark:via-background-dark dark:to-transparent z-10">
        <button
          onClick={handleLogActivity}
          disabled={isSyncing}
          className="flex w-full h-14 items-center justify-center rounded-full bg-primary text-background-dark gap-2 text-base font-bold leading-normal tracking-[0.015em] disabled:opacity-50 disabled:cursor-not-allowed shadow-glow-primary"
        >
          {isSyncing ? (
            <>
              <span className="material-symbols-outlined animate-spin">
                sync
              </span>
              Syncing Activities...
            </>
          ) : (
            <>
              <span className="material-symbols-outlined">add_task</span>
              Log Today's Activity
            </>
          )}
        </button>
      </div>

      {/* Toast Notification */}
      <ErrorToast
        isOpen={toastOpen}
        onClose={() => setToastOpen(false)}
        message={toastMessage}
        variant={toastVariant}
        position="top"
        duration={5000}
      />

      {/* Participant Profile Modal */}
      <ParticipantProfileModal
        isOpen={!!selectedParticipant}
        onClose={() => setSelectedParticipant(null)}
        participant={selectedParticipant}
        challengeContext={{
          name: leaderboardData?.challengeName || progressData?.challengeName || "Challenge",
          totalDistanceGoal: leaderboardData?.totalDistanceGoal || progressData?.distanceGoal || 0,
        }}
      />
    </div>
  );
}
